/**
 * Single canonical definition of the Ova Balance ecosystem.
 *
 * Every surface (bundle pages, PDP cross-links, the PCOS collection, schema,
 * metadata, llms.txt, agent manifest) reads names, slugs, SKUs, membership and
 * copy from here so the same product entity is never described two ways.
 *
 * Commerce values (price, savings, availability) are NEVER stored here — they
 * come from the live bundle rule + published catalogue at request time.
 */

export const SITE = "https://truwe.in";
export const ORGANIZATION_ID = `${SITE}/#organization`;

export type CoreHandle = "ova-balance" | "mag-absorb-pro" | "liver-biome-plus";

export const CORE_PRODUCTS: Record<CoreHandle, { name: string; url: string; id: string }> = {
  "ova-balance": {
    name: "Ova Balance",
    url: `${SITE}/products/ova-balance`,
    id: `${SITE}/products/ova-balance#product`,
  },
  "mag-absorb-pro": {
    name: "Mag Absorb Pro",
    url: `${SITE}/products/mag-absorb-pro`,
    id: `${SITE}/products/mag-absorb-pro#product`,
  },
  "liver-biome-plus": {
    name: "Liver Biome Plus",
    url: `${SITE}/products/liver-biome-plus`,
    id: `${SITE}/products/liver-biome-plus#product`,
  },
};

export interface EcosystemBundle {
  /** Canonical slug — also the live bundle_rules slug. */
  slug: string;
  /** Previous public slug, permanently redirected to `slug`. */
  legacySlug?: string;
  /** Customer-facing name (H1 + rule title). */
  name: string;
  /** Visible supporting line under the H1. */
  supportLine: string;
  components: CoreHandle[];
  /** Persistent bundle SKU. */
  sku: string;
  seoTitle: string;
  seoDescription: string;
  breadcrumbLabel: string;
  /** 1-2 sentence plain answer to "what is this?". */
  summary: string;
  /** Why these formulas are paired (no treatment claims). */
  whyPaired: string;
  /** Who it is for, in nutrition-support language. */
  whoFor: string;
  /** How it differs from Ova Balance on its own. */
  versusCore: string;
  /** Short CTA used on cards. */
  cta: string;
  /** Collection card blurb. */
  cardBlurb: string;
}

export const OVA_BUNDLES: EcosystemBundle[] = [
  {
    slug: "balance-calm-duo",
    name: "Balance & Calm Duo",
    supportLine: "Ova Balance + Mag Absorb Pro",
    components: ["ova-balance", "mag-absorb-pro"],
    sku: "TRW-BND-OBCALM",
    seoTitle: "PCOS Support Combo: Inositol + Magnesium | TruWe",
    seoDescription:
      "Pair Ova Balance 40:1 inositol with Mag Absorb Pro for a daily routine supporting cycle, metabolic, magnesium, sleep and recovery needs.",
    breadcrumbLabel: "Balance & Calm Duo",
    summary:
      "Balance & Calm Duo is a two-product TruWe combo: Ova Balance for daily cycle and metabolic nutrition, paired with Mag Absorb Pro for magnesium, sleep and recovery support.",
    whyPaired:
      "Ova Balance carries the inositol-led foundation at a 40:1 Myo to D-Chiro ratio. Mag Absorb Pro adds a five-form magnesium layer with its cofactors, so the evening side of the routine is covered by a formula designed for it rather than by a single added mineral.",
    whoFor:
      "For adults following a daily cycle and metabolic nutrition routine who also want dedicated magnesium support for relaxation, muscle function and recovery.",
    versusCore:
      "Ova Balance remains the core formula. The duo simply adds complementary magnesium nutrition alongside it; it does not change the Ova Balance formulation or its directions.",
    cta: "Explore Duo",
    cardBlurb: "Cycle and metabolic nutrition with a dedicated evening magnesium layer.",
  },
  {
    slug: "balance-detox-duo",
    legacySlug: "her-balance",
    name: "Balance & Detox Duo",
    supportLine: "Ova Balance + Liver Biome Plus",
    components: ["ova-balance", "liver-biome-plus"],
    sku: "TRW-BND-OBLIVER",
    seoTitle: "PCOS Support Combo: Inositol + Liver Support | TruWe",
    seoDescription:
      "Pair Ova Balance 40:1 inositol with Liver Biome Plus for a daily routine supporting cycle, metabolic, liver and gut wellness.",
    breadcrumbLabel: "Balance & Detox Duo",
    summary:
      "Balance & Detox Duo is a two-product TruWe combo: Ova Balance for daily cycle and metabolic nutrition, paired with Liver Biome Plus for liver and gut wellness support.",
    whyPaired:
      "Ova Balance keeps cycle and metabolic nutrition at the centre. Liver Biome Plus contributes NAC, liposomal silymarin, curcumin, alpha-lipoic acid and a multi-strain probiotic, so liver and gut wellness are supported by a formula built around them.",
    whoFor:
      "For adults who want daily cycle and metabolic nutritional support alongside complementary liver and gut wellness nutrition.",
    versusCore:
      "Ova Balance is the core formula on its own. The duo adds a separate liver and gut wellness formula to the same daily routine; neither formulation changes.",
    cta: "Explore Duo",
    cardBlurb: "Cycle and metabolic nutrition alongside liver and gut wellness support.",
  },
  {
    slug: "full-ova-balance-protocol",
    legacySlug: "full-reset",
    name: "Full Ova Balance Protocol",
    supportLine: "Ova Balance + Mag Absorb Pro + Liver Biome Plus",
    components: ["ova-balance", "mag-absorb-pro", "liver-biome-plus"],
    sku: "TRW-BND-OBFULL",
    seoTitle: "PCOS Support Kit | Full Ova Balance Protocol | TruWe",
    seoDescription:
      "Combine Ova Balance, Mag Absorb Pro and Liver Biome Plus in one structured daily protocol for cycle, metabolic, magnesium, liver and gut support.",
    breadcrumbLabel: "Full Ova Balance Protocol",
    summary:
      "Full Ova Balance Protocol is TruWe's three-product PCOS support kit: Ova Balance, Mag Absorb Pro and Liver Biome Plus in one structured daily protocol.",
    whyPaired:
      "Each formula covers a different layer of the routine: inositol-led cycle and metabolic nutrition, five-form magnesium for relaxation and recovery, and a liver and gut wellness formula with NAC, liposomal silymarin and probiotics.",
    whoFor:
      "For adults who want the complete three-product daily protocol rather than a single formula, and prefer to keep cycle, magnesium, liver and gut nutrition in one routine.",
    versusCore:
      "Ova Balance alone is the core formula. The full protocol keeps it at the centre and adds two complementary formulas around it, each with its own directions.",
    cta: "Explore Full Protocol",
    cardBlurb: "The complete three-product daily protocol built around Ova Balance.",
  },
];

export function bundleBySlug(slug: string): EcosystemBundle | undefined {
  return OVA_BUNDLES.find((b) => b.slug === slug);
}

/** Canonical slug for a legacy bundle URL, or null when it isn't a rename. */
export function canonicalSlugForLegacy(slug: string): string | null {
  const match = OVA_BUNDLES.find((b) => b.legacySlug === slug);
  return match ? match.slug : null;
}

/** Bundles that contain a given core product, in ecosystem order. */
export function bundlesContaining(handle: string): EcosystemBundle[] {
  return OVA_BUNDLES.filter((b) => (b.components as string[]).includes(handle));
}

export function bundleUrl(b: EcosystemBundle): string {
  return `${SITE}/products/${b.slug}`;
}

export function bundleProductId(b: EcosystemBundle): string {
  return `${bundleUrl(b)}#product`;
}

/** schema.org `isRelatedTo` nodes for a bundle's component products. */
export function bundleRelatedProducts(b: EcosystemBundle) {
  return b.components.map((handle) => ({
    "@type": "Product",
    "@id": CORE_PRODUCTS[handle].id,
    name: CORE_PRODUCTS[handle].name,
    url: CORE_PRODUCTS[handle].url,
  }));
}
